<script setup lang="ts">
import type { UISearchItem } from '../../types/inputs'
import { UIInput } from '#components'
import { ref, computed, useActiveElement } from '#imports'

export interface UISearchProps {
    items: UISearchItem[]
    label?: string
    noResultsText?: string
    minCharsForSearch?: number
    showSearchbarPopup?: boolean
}
const props = withDefaults(defineProps<UISearchProps>(), {
    label: 'Søg i vores indhold',
    noResultsText: 'Din søgning gav desværre ingen resultater.',
    minCharsForSearch: 3,
    showSearchbarPopup: true,
})

const emit = defineEmits([
    'select-result',
    'search-results',
])

const modelValue = defineModel<string>()
const hasMinChars = computed(() => {
    if (!modelValue.value) return false

    return modelValue.value.length >= props.minCharsForSearch
})
const selectedResult = ref(null)
const searchResults = ref(null)

function selectResult(result: any) {
    selectedResult.value = result
    emit('select-result', result)
}

function onSearchResults(result: any) {
    searchResults.value = result
    emit('search-results', result)
}

function sortBySubstringIdx(arr: UISearchItem[], str: string) {
    return arr.sort((a, b) => {
        const aIdx = a.title.toLowerCase().indexOf(str)
        const bIdx = b.title.toLowerCase().indexOf(str)

        // Prioritize B if it contains str earlier than A
        if (bIdx >= 0 && (bIdx < aIdx || aIdx < 0)) {
            return 1
        }

        // Prioritize A if it contains str earlier than B
        if (aIdx >= 0 && (aIdx < bIdx || bIdx < 0)) {
            return -1
        }

        // Dont prioritize if none contains str or contains at same idx
        return 0
    })
}

const results = computed(() => {
    const searchLower = modelValue.value?.toLowerCase() || ''

    if (!hasMinChars.value) {
        return []
    }

    const sortedResult = sortBySubstringIdx(props.items.filter((item: UISearchItem) =>
        item.title.toLowerCase().includes(searchLower)
        || item.body?.toLowerCase().includes(searchLower)
        || item.keywords?.some(tag => tag.includes(searchLower))), searchLower)

    onSearchResults(sortedResult)

    return sortedResult
})

const inputRef = ref<InstanceType<typeof UIInput> | null>(null)

const activeElement = useActiveElement()
const isActiveElement = computed(() => inputRef.value?.$el.contains(activeElement.value))
</script>

<template>
    <div class="search-bar-wrapper">
        <UIInput
            ref="inputRef"
            v-model="modelValue"
            type="search"
            name="search"
        >
            <template #prepend="{ iconAttrs }">
                <UIIcon v-bind="iconAttrs" name="search" />
            </template>
            {{ props.label }}
        </UIInput>

        <slot :results="results" :selected-result="selectedResult" :is-active="isActiveElement">
            <Transition name="fade">
                <div v-if="hasMinChars && isActiveElement && showSearchbarPopup" class="search-bar-results">
                    <div v-if="results.length" class="search-bar-results-wrapper">
                        <div
                            v-for="(result, index) in results"
                            :key="`result_${index}`"
                            class="search-bar-result"
                            @click="selectResult(result)"
                        >
                            <div class="search-bar-result__title">
                                {{ result.title }}
                            </div>

                            <div v-if="result.body" class="search-bar-result__body">
                                {{ result.body }}
                            </div>
                        </div>
                    </div>

                    <div v-else class="search-bar-result--empty">
                        {{ noResultsText }}
                    </div>
                </div>
            </Transition>
        </slot>
    </div>
</template>

<style lang="scss" scoped>
.search-bar {
    width: 100%;
    padding: 15px 15px 15px 50px;
    font-size: $font-size-lg;
    position: relative;
    border-radius: var(--border-radius, 8px);
    border: 0;

    &-wrapper {
        position: relative;
        width: 100%;

        :deep(.input-field__input-wrapper) .input-field__input {
            border: 0;
        }
    }

    &-label {
        text-align: center;
        margin-top: 20px;
    }

    &-results {
        position: absolute;
        top: calc(100% + 20px);
        color: var(--on-surface);
        background-color: var(--surface, $suggestion-box-bg);
        border-radius: var(--border-radius, 4px);
        width: inherit;
        padding: 10px 0;
        min-height: 50px;
        border: 1px solid var(--border-surface-soft, rgba(0, 0, 0, 0.22));
        box-shadow: 0px 4px 10px var(--border-surface-soft, rgba(122, 122, 122, 0.22));
        z-index: 1;

        &::before {
            content: '';
            position: absolute;
            top: -8px;
            left: 25px;
            width: 14px;
            height: 14px;
            color: var(--on-surface);
            background-color: var(--surface, white);
            transform: rotate(-45deg);
            border: 1px solid var(--border-surface-soft, rgba(0, 0, 0, 0.22));
            border-bottom: 0;
            border-left: 0;
            z-index: -1;
        }

        &-wrapper {
            display: flex;
            flex-direction: column;
            overflow-y: auto;
            max-height: 200px;
            padding: 0 16px;

            @media (min-width: $screen-md) {
                max-height: 400px;
            }
        }
    }

    &-result {
        padding: 8px 0 8px;
        font-size: $font-size-sm;
        cursor: pointer;
        user-select: none;

        &__title {
            font-weight: $text-bold;
        }

        &__body {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        &:hover {
            color: var(--secondary, $brand-primary);
        }

        &:last-child {
            border-bottom: 0;
        }

        &--empty {
            background-color: var(--surface, $suggestion-box-bg);
            padding: 3px 5px;
            border-radius: var(--border-radius, 4px);
            margin: 0 10px;
        }
    }
}
</style>
